import Money from "../ui/Money"
import { ReactNode, useEffect, useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import { FiChevronDown } from "react-icons/fi"
import {
  getPatentFeeBreakdown,
  updatePatentAdministrativeFees,
  updatePatentFeeSelections,
  updatePatentRefundDeclarations,
  PatentFeeBreakdown,
  PatentFeeLine
} from "../../services/stripePaymentService"
import { formatApiError } from "../../utils/formatApiError"

const EP_PHASE_FEE_GROUPS = [
  "Payment details",
  "Refund details",
  "Procedure fees",
  "Administrative fees",
  "Annual fees",
  "Extension/validation fees",
  "Further processing fees"
]

export default function PatentEpPhaseFeesSection({
  applicationId,
  disabled = false
}: {
  applicationId: string | null
  disabled?: boolean
}) {
  const [feeBreakdown, setFeeBreakdown] = useState<PatentFeeBreakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!applicationId) {
      setFeeBreakdown(null)
      return
    }

    let cancelled = false
    setLoading(true)

    getPatentFeeBreakdown(applicationId)
      .then((result) => {
        if (!cancelled) setFeeBreakdown(result)
      })
      .catch(() => {
        if (!cancelled) setFeeBreakdown(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  async function handleToggleRefundToEpoDepositAccount(checked: boolean) {
    if (!applicationId || updating || disabled || !feeBreakdown) return

    setUpdating(true)
    setError(null)

    try {
      setFeeBreakdown(
        await updatePatentRefundDeclarations(applicationId, {
          ...feeBreakdown.refundDeclarations,
          refundToEpoDepositAccount: checked
        })
      )
    } catch (updateError) {
      setError(
        formatApiError(
          updateError,
          "The refund details could not be updated. Please reload the page and try again."
        )
      )
    } finally {
      setUpdating(false)
    }
  }

  async function handleUpdateFeeSelections(furtherProcessingFeeSelected: boolean) {
    if (!applicationId || updating || disabled) return

    setUpdating(true)
    setError(null)

    try {
      setFeeBreakdown(
        await updatePatentFeeSelections(applicationId, {
          furtherProcessingFeeSelected
        })
      )
    } catch (updateError) {
      setError(
        formatApiError(
          updateError,
          "The fee selection could not be updated. Please reload the page and try again."
        )
      )
    } finally {
      setUpdating(false)
    }
  }

  async function handleToggleAdministrativeFee(checked: boolean) {
    if (!applicationId || updating || disabled) return

    setUpdating(true)
    setError(null)

    try {
      setFeeBreakdown(await updatePatentAdministrativeFees(applicationId, checked))
    } catch (updateError) {
      setError(
        formatApiError(
          updateError,
          "The administrative fee could not be updated. Please reload the page and try again."
        )
      )
    } finally {
      setUpdating(false)
    }
  }

  function handleToggleFurtherProcessingFee(checked: boolean) {
    if (!feeBreakdown) return

    void handleUpdateFeeSelections(checked)
  }

  if (!applicationId) {
    return (
      <div className="text-sm text-gray-500">
        Save the application (fill in a reference number or title) to see the fee overview.
      </div>
    )
  }

  return (
    <div>
      <div className="mb-2 text-sm font-semibold text-gray-900">Fees</div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="divide-y divide-gray-200 border-y border-gray-200">
        {EP_PHASE_FEE_GROUPS.map((label) => (
          <FeeGroupSection key={label} label={label}>
            {label === "Payment details" ? (
              loading ? (
                <div className="text-sm text-gray-500"><SpinnerText>Loading fee overview...</SpinnerText></div>
              ) : feeBreakdown ? (
                <div className="flex items-start gap-3 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  <span>{feeBreakdown.paymentNote}</span>
                </div>
              ) : (
                <div className="min-h-6" />
              )
            ) : label === "Refund details" && feeBreakdown ? (
              <div className="flex flex-col gap-3">
                <label
                  className={`flex items-start gap-3 text-sm ${
                    disabled ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={feeBreakdown.refundDeclarations.refundToEpoDepositAccount}
                    disabled={updating || disabled}
                    onChange={(event) =>
                      handleToggleRefundToEpoDepositAccount(event.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 accent-blue-800"
                  />
                  Any refund to the deposit account at the EPO
                </label>
              </div>
            ) : label === "Procedure fees" ? (
              <SelectableFeeTable
                emptyText={loading ? <SpinnerText>Loading fee overview...</SpinnerText> : "No procedure fees calculated yet."}
                lines={feeBreakdown?.procedureFees.lines || []}
                disabled
              />
            ) : label === "Administrative fees" ? (
              <SelectableFeeTable
                emptyText={loading ? <SpinnerText>Loading fee overview...</SpinnerText> : "No administrative fees available."}
                lines={feeBreakdown?.administrativeFees.lines || []}
                disabled={disabled || updating || !feeBreakdown}
                onToggle={(checked) => void handleToggleAdministrativeFee(checked)}
              />
            ) : label === "Annual fees" ? (
              <p className="text-sm text-gray-600">
                This fee is not yet due. No renewal fees are payable for the
                first two years. The renewal fee for the third year is due on
                the last day of the month containing the second anniversary of
                the filing date and may be paid up to six months before that
                date.
              </p>
            ) : label === "Extension/validation fees" ? (
              <SelectableFeeTable
                emptyText="No extension or validation states selected."
                lines={feeBreakdown?.extensionValidationFees.lines || []}
                disabled={disabled || updating || !feeBreakdown}
              />
            ) : label === "Further processing fees" ? (
              <SelectableFeeTable
                emptyText="Select an extension or validation fee to calculate further processing fees."
                lines={feeBreakdown?.furtherProcessingFees.lines || []}
                disabled={disabled || updating || !feeBreakdown}
                onToggle={(checked) => handleToggleFurtherProcessingFee(checked)}
              />
            ) : (
              <div className="min-h-6" />
            )}
          </FeeGroupSection>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t-2 border-gray-300 pt-3 text-sm font-semibold text-gray-900">
        <span>Total</span>
        <span className="tabular-nums">
          {feeBreakdown ? <Money amount={feeBreakdown.total} /> : "-"}
        </span>
      </div>
    </div>
  )
}



function SelectableFeeTable({
  lines,
  disabled,
  emptyText,
  onToggle
}: {
  lines: PatentFeeLine[]
  disabled: boolean
  emptyText: ReactNode
  onToggle?: (checked: boolean) => void
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[42rem] text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-500">
            <th className="w-12 px-3 py-3"></th>
            <th className="px-3 py-3">Fee code and description</th>
            <th className="px-3 py-3 text-right">Amount in EUR</th>
            <th className="px-3 py-3 text-right">Reduction</th>
            <th className="px-3 py-3 text-right">Quantity</th>
            <th className="px-3 py-3 text-right">Amount due</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lines.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-sm text-gray-500">
                {emptyText}
              </td>
            </tr>
          ) : (
            lines.map((line) => (
              <tr
                key={line.code}
                className={line.selected || line.selectable ? "text-dantzBlue" : "text-gray-300"}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={line.selected}
                    disabled={disabled || !line.selectable}
                    onChange={(event) => onToggle?.(event.target.checked)}
                    className="h-4 w-4 border-gray-300 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-3 py-2">
                  {line.code} - {line.label}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {(line.baseAmount ?? line.amount) !== null && (
                    <span className="inline-block min-w-28 bg-gray-50 px-3 py-2 text-left">
                      <Money amount={(line.baseAmount ?? line.amount) as number} />
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {line.reductionFactor ? `${Math.round(line.reductionFactor * 100)}%` : ""}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {line.selected && (
                    <span className="inline-block min-w-16 bg-gray-50 px-3 py-2 text-left">
                      {line.quantity ?? 1}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {line.amount !== null && line.selected
                    ? <Money amount={line.amount} />
                    : ""}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

function FeeGroupSection({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">{label}</span>

        <FiChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && <div className="pb-4">{children}</div>}
    </div>
  )
}
